// Formatting helpers for the Anniversary Bonuses view. Bonus amounts come from the API in USD
// cents; NGR (ngr_usd_1y) is already whole USD.

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const usdWhole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export const formatCents = (cents) =>
  cents === null || cents === undefined ? '—' : usd.format(cents / 100);

export const formatUSD = (dollars) =>
  dollars === null || dollars === undefined ? '—' : usdWhole.format(dollars);

// dollarsToCents parses a user-typed dollar amount ("200", "199.99", "1,250") into integer cents.
// Returns null for anything that is not a positive amount with at most two decimals.
export const dollarsToCents = (input) => {
  const cleaned = String(input ?? '').replace(/[\s,$]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const cents = Math.round(parseFloat(cleaned) * 100);
  return cents > 0 ? cents : null;
};

export const centsToDollarsInput = (cents) =>
  cents === null || cents === undefined ? '' : (cents / 100).toFixed(2).replace(/\.00$/, '');

export const STATUS_OPTIONS = ['pending', 'approved', 'overridden', 'rejected', 'scheduled', 'issued'];

export const STATUS_BADGE = {
  pending: { label: 'Pending', variant: 'secondary' },
  // approved: a VIP manager signed off the proposed amount; issued like pending.
  approved: { label: 'Approved', variant: 'default' },
  overridden: { label: 'Overridden', variant: 'info' },
  rejected: { label: 'Rejected', variant: 'destructive' },
  // scheduled: booked in loyalty.anniversary_bonus_history, its day still ahead (derived by the API).
  scheduled: { label: 'Will be issued', variant: 'warning' },
  issued: { label: 'Issued', variant: 'success' },
};

export const ANNIVERSARY_TYPE_LABEL = { birthday: 'Birthday', signup: 'Sign-up' };

export const anniversaryTypeLabel = (type) =>
  ANNIVERSARY_TYPE_LABEL[type] || (type ? type.replace(/[_-]/g, ' ') : '—');

// "level_50" -> "50"
export const formatLevel = (levelId) => (levelId ? levelId.replace(/^level_/, '') : '—');

// "2026-09-28T14:05:00Z" -> "2026-09-28 14:05" (UTC)
export const formatTimestamp = (ts) => (ts ? new Date(ts).toISOString().slice(0, 16).replace('T', ' ') : '');

export const todayISO = () => new Date().toISOString().slice(0, 10);

// lockReasonText explains why a row cannot be moderated, from the API's lock_reason code. There
// are only two messages: already issued (in the bonus history), or 1 day or less to go.
export const lockReasonText = (row) =>
  row.lock_reason === 'issued' ? 'Already issued' : 'Changes close 1 day before the anniversary';

// Sortable columns (API sort_col keys). Amounts, NGR and level start high-to-low, the date soonest first.
const SORT_FIRST_DIR = { proposed: 'desc', override: 'desc', ngr: 'desc', level: 'desc', date: 'asc' };

// nextSort: clicking the active column flips its direction; another column starts in its natural one.
export const nextSort = ({ sortCol, sortDir }, col) =>
  sortCol === col
    ? { sortCol: col, sortDir: sortDir === 'asc' ? 'desc' : 'asc' }
    : { sortCol: col, sortDir: SORT_FIRST_DIR[col] || 'asc' };

// playerSearchParams: the Player box takes an ID or an email. Digits only search user_id exactly;
// anything else matches part of the email (case-insensitive, done by the API).
export const playerSearchParams = (text) => {
  const t = String(text ?? '').trim();
  if (!t) return {};
  return /^\d+$/.test(t) ? { user_id: t } : { email: t };
};
